import React from 'react';
import { Sparkles } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Warm, on-brand empty state. Pass an illustration (lucide icon
 * component), a headline, and optional supporting text + CTA.
 * Optional `onSeed` renders a "Try with sample data" secondary CTA
 * — perfect for a fresh tenant's first login.
 */
export default function EmptyState({
  icon: Icon,
  title = 'Nothing here yet',
  description,
  ctaLabel,
  onCta,
  seedLabel = 'Try with sample data',
  onSeed,
  className = '',
  testId = 'empty-state',
}) {
  return (
    <div
      data-testid={testId}
      className={`flex flex-col items-center justify-center text-center py-12 px-6 ${className}`}
    >
      <div className="relative mb-4">
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-violet-100 to-fuchsia-100 dark:from-violet-950/40 dark:to-fuchsia-950/30 blur-xl" />
        <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl border border-violet-200 dark:border-violet-800 bg-white dark:bg-slate-900 shadow-sm">
          {Icon ? (
            <Icon className="h-7 w-7 text-violet-500" />
          ) : (
            <Sparkles className="h-7 w-7 text-violet-500" />
          )}
        </div>
      </div>
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {description && (
        <p className="mt-1.5 text-sm text-muted-foreground max-w-md">
          {description}
        </p>
      )}
      {(onCta || onSeed) && (
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
          {onCta && (
            <Button size="sm" onClick={onCta} data-testid={`${testId}-cta`}>
              {ctaLabel || 'Get started'}
            </Button>
          )}
          {onSeed && (
            <Button
              size="sm"
              variant="outline"
              onClick={onSeed}
              data-testid={`${testId}-seed`}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" /> {seedLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
